import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SortDirection } from '@angular/material/sort';
import { Subject, take, takeUntil } from 'rxjs';
import { SchoolsService, ToastMessageService, AuthService, errorToastValue, LoadingSpinnerService, SystemService } from '@k12/app-services';
import { AppInsightsService } from '@k12/error_handler';
import { Person, SchoolContact, SchoolContactsResponse, SvgIcon, ToastMessageType, SeverityLevel, ModalType } from '@k12/models';
import { MessageCenterSendDialogComponent } from '@k12/messaging';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { ModalDialogComponent } from '@k12/ui-primitives';
import { PromoteHdmaDialogComponent } from '@k12/user-info';
import { NewEditContact } from './edit-contact/new-edit-contact.component';

export const GLOBAL_ADMIN_GROUP = 'K12-admin-Global-admin';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-contacts',
  imports: [CfiMatTableComponent, CommonModule, MatButtonModule, MatIconModule, ReactiveFormsModule, MatTooltipModule],
  standalone: true,
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss']
})
export class ContactsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() columns: ColumnDefinition[] = [];
  @Input() contactBackdropClass: string = '';
  @Input() contactDialogPanelClass: string = '';
  @Input() isAdminView: boolean = false;
  @Input() isInstitutionView: boolean = false;
  @Input() includeHdmaInList: boolean = false;
  @Input() sortKey: string = 'isPrimaryContact';
  @Input() userId: string | null = null;
  @Input() userTypeId: number | null = null;

  // Emit event to refresh the message tile on success
  @Output() messageSent = new EventEmitter<void>();

  currentHDMAContact: SchoolContact | null = null;
  contactsTableData: WritableSignal<SchoolContact[]> = signal([]);
  displayedColumns: string[] = [];
  isSuperAdmin: boolean = false;
  sortDir: SortDirection = 'asc';
  SvgIcon = SvgIcon;

  readonly sendMessageDialog = inject(MatDialog);

  private dialogRef!: MatDialogRef<ModalDialogComponent>;
  private sendMessageDialogRef!: MatDialogRef<MessageCenterSendDialogComponent>;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly authService = inject(AuthService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly systemService = inject(SystemService);
  private readonly dialogService = inject(MatDialog);
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly schoolsService = inject(SchoolsService);
  private readonly toastService = inject(ToastMessageService);

  ngOnInit(): void {
    this.displayedColumns = this.columns.map((col) => col.name);
    this.isSuperAdminUser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.displayedColumns = this.columns.map((col) => col.name);
    }

    if (changes['userId'] || changes['isInstitutionView'] || changes['includeHdmaInList']) {
      this.getOtherContacts();
    }
  }

  getTableClasses(): string[] {
    const classes: string[] = ['full-width-table'];
    if (!this.isAdminView) {
      classes.push('contacts-table');
    }
    return classes;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  async isSuperAdminUser(): Promise<void> {
    try {
      this.isSuperAdmin = await this.authService.userHasGroup(GLOBAL_ADMIN_GROUP);
    } catch (error) {
      this.isSuperAdmin = false;
      this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)), SeverityLevel.error);
    }
  }

  deleteContact(contact: SchoolContact): void {
    this.dialogRef = this.dialogService.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        align: 'left',
        checkMark: false,
        closeButtonText: 'Cancel',
        confirmButtonText: 'Continue',
        message: `Deleting this contact will permanently remove all their details from the contact list. Are you sure you want to continue?`,
        modalType: ModalType.ConfirmModal,
        title: 'Delete Contact'
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    this.dialogRef.componentInstance.onConfirmModal.pipe(take(1), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.dialogRef.close();
      this.onDeleteContact(contact);
    });
  }

  editContact(contact: SchoolContact): void {
    this.dialogService
      .open(NewEditContact, {
        panelClass: this.contactDialogPanelClass,
        backdropClass: this.contactBackdropClass,
        disableClose: true,
        data: {
          data: contact,
          isAdminView: this.isAdminView,
          programPartnerId: this.isInstitutionView ? null : this.userId,
          institutionId: this.isInstitutionView ? this.userId : null,
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: SchoolContact | undefined) => {
        if (result) {
          this.getOtherContacts();
        }
      });
  }

  getPersonEmail(contact: SchoolContact): string {
    if (contact?.person?.personEmails && contact.person.personEmails.length > 0) {
      const email = contact.person.personEmails.find((e) => e.isPrimary)?.emailAddress;
      return email && email.length > 0 ? email : 'n/a';
    }
    return 'n/a';
  }

  getPersonPhone(contact: SchoolContact): string {
    if (contact?.person?.personPhones && contact.person.personPhones.length > 0) {
      const primaryPhone = contact.person.personPhones.find((p) => p.isPrimary);
      const phoneNumber = primaryPhone?.phone?.phoneNumber;
      if (!phoneNumber || phoneNumber.length === 0) {
        return '-';
      }

      const extension = primaryPhone?.phone?.extension ?? primaryPhone?.extension;
      if (extension?.trim()) {
        return `${phoneNumber} ext. ${extension.trim()}`;
      }

      return phoneNumber;
    }
    return '-';
  }

  onPromoteContact(contact: SchoolContact): void {
    const contactName = `${contact.person?.firstName || ''} ${contact.person?.lastName || ''}`;
    const currentHDMAName = this.currentHDMAContact
      ? `${this.currentHDMAContact.person?.firstName || ''} ${this.currentHDMAContact.person?.lastName || ''}`
      : 'N/A';
    const dialogRef = this.dialogService.open(PromoteHdmaDialogComponent, {
      width: '60vw',
      minWidth: '600px',
      maxWidth: '800px',
      disableClose: true,
      data: { contactName, currentHDMAName, isAdminView: this.isAdminView, isInstitutionView: this.isInstitutionView },
    });

    // Allow closing with Escape key
    dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        dialogRef.close();
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed: boolean) => {
        if(confirmed && this.userId && contact.personId){
          this.promoteToHDMA(this.userId ?? "", contact.personId);
        }
      });
  }

  // Button function for sending messages
  onMessageButtonClicked(contact: SchoolContact): void {
    if (contact.programPartnerId) {
      const selectedContactName = [contact.person?.firstName, contact.person?.lastName]
        .filter((name): name is string => !!name)
        .join(' ')
        .trim();
      const hdmaContactName = [
        this.currentHDMAContact?.person?.firstName,
        this.currentHDMAContact?.person?.lastName
      ]
        .filter((name): name is string => !!name)
        .join(' ')
        .trim();

      this.sendMessageDialogRef = this.sendMessageDialog.open(MessageCenterSendDialogComponent, {
        width: '60vw',
        maxHeight: '80vh',
        data: {
          programPartnerId: contact.programPartnerId,
          programPartnerTypeId: this.userTypeId,
          recipientId: contact.accountId ?? null,
          recipientName: selectedContactName || null,
          hdmaId: this.currentHDMAContact?.accountId ?? null,
          hdmaName: hdmaContactName || null,
          allowRecipientSelection: false
        },
        disableClose: true,
        panelClass: 'message-center-send-dialog'
      });

      this.sendMessageDialogRef.afterClosed().subscribe({
        next: (result) => {
          // If the result is a string, then it is an error message.
          if (typeof result === 'string') {
            this.handleError(result, result);
          } else if (typeof result === 'boolean' && result) {
            this.toastService.showToast(ToastMessageType.Success, 'Message sent successfully.');
            this.messageSent.emit();
          }
        }
      });
    } else {
      this.handleError('Cannot navigate to message send: missing user id');
    }
  }

  private getOtherContacts(): void {
    this.schoolsService
      .getSchoolContacts(this.userId || '', true, this.isInstitutionView)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: SchoolContactsResponse) => {
          const contacts = response?.entity || [];
          this.currentHDMAContact = contacts.find((c) => c.isHDMA) || null;

          const filteredContacts = contacts
            .filter((c) => this.includeHdmaInList || !c.isHDMA)
            .map((contact) => {
              const personData = (contact.person as Person) || {};
              return {
                ...contact,
                firstName: personData.firstName ?? '',
                lastName: personData.lastName ?? '',
                statusText: this.getStatusText(contact.statusId),
                title: personData.title ?? '-',
                email: this.getPersonEmail(contact),
                phone: this.getPersonPhone(contact)
              };
            });
          this.contactsTableData.set(filteredContacts);
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Failed to load contacts.';
          this.handleError(errorMessage, 'Failed to load contacts. Please try again later.');
          this.contactsTableData.set([]);
        }
      });
  }

  private promoteToHDMA(parentId: string, personId: string): void {
    this.loadingSpinnerService.showSpinner();

    this.schoolsService
      .promoteContactToHDMA(parentId, personId, this.isInstitutionView)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.loadingSpinnerService.hideSpinner();
          if (response.success) {
            this.toastService.showToast(ToastMessageType.Success, 'Contact promoted to HDMA successfully.');
            this.getOtherContacts(); // Refresh the contact list
          } else if (response.validationErrors && response.validationErrors.length > 0) {
            // Handle validation errors - display each validation error
            response.validationErrors.forEach((error: string) => {
              this.toastService.showToast(ToastMessageType.Error, error);
            });
          } else {
            // Fallback to message or generic error
            this.toastService.showToast(
              ToastMessageType.Error,
              response.message || 'Failed to promote contact to HDMA.'
            );
          }
        },
        error: (error: HttpErrorResponse) => {
          this.loadingSpinnerService.hideSpinner();

          // Check if error response has validation errors
          if (error.error?.validationErrors?.length > 0) {
            error.error.validationErrors.forEach((validationError: string) => {
              this.toastService.showToast(ToastMessageType.Error, validationError);
            });
          } else {
            const errorMessage = errorToastValue(error);
            this.toastService.showToast(ToastMessageType.Error, errorMessage);
          }
        }
      });
  }

  private handleError(errorMessage: string, toastMessage?: string): void {
    this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
    if (toastMessage) {
      this.toastService.showToast(ToastMessageType.Error, toastMessage);
    } else {
      this.toastService.showToast(ToastMessageType.Error, errorMessage);
    }
  }

  private getStatusText(statusId: number): string {
    switch (statusId) {
      case 1:
        return 'Invited';
      case 2:
        return 'Active';
      case 3:
        return 'Archived';
      default:
        return 'Unknown';
    }
  }

  private onDeleteContact(contact: SchoolContact): void {
    this.schoolsService
      .deleteSchoolContact(this.userId ?? "", contact.personId, this.isInstitutionView)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          if (response?.success === true) {
            this.toastService.showToast(ToastMessageType.Success, 'Contact deleted successfully.');
            this.getOtherContacts(); // Refresh the contact list after deletion
          } else {
            this.handleError('Failed to delete contact.', 'Contact delete failed. Please try again later.');
          }
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Failed to delete contact.';
          this.handleError(errorMessage, 'Contact delete failed. Please try again later.');
        }
      });
  }

  inviteContact(): void {
    const dialogRef = this.dialogService.open(NewEditContact, {
      panelClass: this.contactDialogPanelClass,
      backdropClass: this.contactBackdropClass,
      disableClose: true,
      data: {
        isAdminView: this.isAdminView,
        programPartnerId: this.isInstitutionView ? null : this.userId,
        institutionId: this.isInstitutionView ? this.userId : null,
      }
    });

    // Allow closing with Escape key
    dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        dialogRef.close();
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: SchoolContact | undefined) => {
        if (result) {
          this.toastService.showToast(ToastMessageType.Success, 'Contact invited successfully.');
          this.getOtherContacts(); // Refresh the contact list
        }
      });
  }
}
